/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useState, useEffect, useRef } from 'react';
import { Grid, Checkbox, Typography, CircularProgress, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { getPlaylists } from '../../../../../../../../../../../actions/playlist_management';
import { useScroll } from '../../../../../../../../../../../../libs/CustomHooks/useScroll';
import useDidMountEffect from '../../../../../../../../../../../../libs/CustomHooks/UseDidMountEffect';
import useResourceHelper from '../../../../../ContentCards/useResourceHelper';

// Interface for playlist data structure
interface PlaylistNode {
  _id: string;
  name: string;
  contentCount?: number; // Number of videos in the playlist
  videos?: any[]; // Array of video references
}

// Interface for pagination state
interface PaginationState {
	total: number;
	limit: number;
	offset: number;
}

interface PlaylistTreeViewProps {
  onSelect?: (selectedIds: string[], selectedData?: PlaylistNode[]) => void;
  initialSelectedIds?: string[];
  searchQuery?: string; // Add search query prop
}

const PlaylistTreeView: React.FC<PlaylistTreeViewProps> = (props) =>
{
	const [ selected, setSelected ] = useState<Record<string, boolean>>({});
	const { onSelect, initialSelectedIds = [], searchQuery } = props;
	const [ playlists, setPlaylists ] = useState<PlaylistNode[]>([]);
	const [ isLoading, setIsLoading ] = useState<boolean>(true);
	const [ error, setError ] = useState<string | null>(null);

	// Pagination state
	const [ page, setPage ] = useState(0);
	const itemsPerPage = 10;
	const [ pagination, setPagination ] = useState<PaginationState>({
		total  : 0,
		limit  : 0,
		offset : 0
	});

	// Refs for infinite scrolling
	const lastEleRef = useRef(null);
	const loaderRef = useRef<boolean>(false);

	// Get company ID from Redux state
	const companyId = useSelector((state: any) => state.mysession.session?.companyId);
	const { isAdminView } = useResourceHelper();

	// Initialize selected state from initialSelectedIds
	useEffect(() =>
	{
		const initialSelectedState: Record<string, boolean> = {};

		if (initialSelectedIds && initialSelectedIds.length > 0)
		{
			initialSelectedIds.forEach((id) =>
			{
				initialSelectedState[id] = true;
			});
		}

		setSelected(initialSelectedState);

		// Notify parent component of the current selection state
		if (onSelect && typeof onSelect === 'function')
		{
			const selectedIds = Object.keys(initialSelectedState).filter((key) => initialSelectedState[key]);
			const selectedData = playlists.filter((playlist) => selectedIds.includes(playlist._id));

			onSelect(selectedIds, selectedData);
		}
	}, [ playlists ]);
	// }, [ initialSelectedIds, onSelect, playlists ]); // this was causing infinite re-render

	// Fetch playlists with pagination
	const fetchPlaylists = async () =>
	{
		if (!companyId)
		{
			setError('Company ID not available');
			setIsLoading(false);

			return;
		}

		try
		{
			setIsLoading(true);
			loaderRef.current = true;

			// Fetch playlists using the API with pagination
			const response = await getPlaylists(companyId, {
				offset : page,
				limit  : itemsPerPage,
				...(searchQuery && searchQuery.trim() && { search: searchQuery }),
				from   : isAdminView ? 'Configuration' : 'MyResource'
			});

			if (response && response.data)
			{
				// Transform data to match the expected format
				const formattedData = response.data.map((playlist: any) => ({
					_id          : playlist._id,
					name         : playlist.name,
					contentCount : playlist.videos?.length || playlist?.contentCount || 0,
					videos       : playlist.videos || []
				}));

				// If it's the first page, replace the data, otherwise append
				const newPlaylists = page === 0
					? formattedData
					: [ ...playlists, ...formattedData ];

				setPlaylists(newPlaylists);
				setError(null);

				// Set pagination data if available
				if (response.pagination)
				{
					setPagination(response.pagination);
				}
			}
			else
			{
				setError('Invalid response from server');
			}
		}
		catch (err: any)
		{
			console.error('Error fetching playlists:', err);
			setError(err.message || 'Failed to load playlists');
		}
		finally
		{
			setIsLoading(false);
			loaderRef.current = false;
		}
	};

	// Use the custom scroll hook for infinite scrolling
	useScroll({
		lastEleRef,
		loaderRef,
		count : pagination.total,
		data  : playlists,
		setPage
	});

	// Fetch playlists on component mount and when dependencies change
	useEffect(() =>
	{
		if (companyId)
		{
			fetchPlaylists();
		}
	}, [ companyId, page ]);

	useDidMountEffect(() =>
	{
		if (companyId)
		{
			page > 0
				? setPage(0)
				: fetchPlaylists();
		}
	}, [ searchQuery ]);

	// Handle selection of playlists
	const toggleSelection = (id: string) =>
	{
		setSelected((prev) =>
		{
			const newState = { ...prev, [id]: !prev[id] };

			// Call the onSelect callback if provided
			if (onSelect && typeof onSelect === 'function')
			{
				const selectedIds = Object.keys(newState).filter((key) => newState[key]);
				const selectedData = playlists.filter((playlist) => selectedIds.includes(playlist._id));

				onSelect(selectedIds, selectedData);
			}

			return newState;
		});
	};

	if (isLoading)
	{
		return (
			<Box display='flex' justifyContent='center' alignItems='center' p={3}>
				<CircularProgress size={24} />
				<Typography variant='body2' ml={1.5} color='text.secondary'>
					Loading playlists...
				</Typography>
			</Box>
		);
	}

	if (error)
	{
		return (
			<Box p={2}>
				<Typography variant='body2' color='error'>Error: {error}</Typography>
			</Box>
		);
	}

	if (playlists.length === 0)
	{
		return (
			<Box p={2} display='flex' alignItems='center' justifyContent='center'>
				<Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
					No playlists found
				</Typography>
			</Box>
		);
	}

	return (
		<Grid container direction='column'>
			{playlists.map((playlist) => (
				<Grid container alignItems='center' key={playlist._id} sx={{ py: 0.5 }}>
					<Grid item>
						<Checkbox
							checked={selected[playlist._id] || false}
							onChange={() => toggleSelection(playlist._id)}
						/>
					</Grid>
					<Grid item xs>
						<Box
							onClick={() => toggleSelection(playlist._id)}
							sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', pr: 2 }}
						>
							<Typography variant='body1' sx={{
								color      : selected[playlist._id] ? '#24A4C4' : 'text.primary',
								fontWeight : selected[playlist._id] ? 500 : 400
							}}
							>
								{playlist.name}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								{playlist.contentCount} {playlist.contentCount === 1 ? 'video' : 'videos'}
							</Typography>
						</Box>
					</Grid>
				</Grid>
			))}

			{/* Element for intersection observer */}
			{!isLoading && playlists.length > 0 &&
			playlists.length < pagination.total && (
				<div ref={lastEleRef} style={{ height: '2px', width: '100%' }} />
			)}

			{/* Loading indicator for infinite scroll */}
			{isLoading && playlists.length > 0 && (
				<Box sx={{ width: '100%', textAlign: 'center', p: 2 }}>
					<CircularProgress size={20} />
					<Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
						Loading more playlists...
					</Typography>
				</Box>
			)}
		</Grid>
	);
};

export default PlaylistTreeView;